import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getSales, deleteSale, updateSaleStatus, getSalesStats } from '../../services/api';

const Sales = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [statusData, setStatusData] = useState({ status: '', payment_status: '' });
  const navigate = useNavigate();

  const statusColors = {
    Draft: 'default',
    Confirmed: 'info',
    Processing: 'warning',
    Shipped: 'primary',
    Delivered: 'success',
    Cancelled: 'error'
  };

  const paymentStatusColors = {
    Pending: 'warning',
    Partial: 'info',
    Paid: 'success'
  };

  const fetchSales = async () => {
    try {
      setLoading(true);
      const [salesResponse, statsResponse] = await Promise.all([
        getSales(),
        getSalesStats()
      ]);
      
      if (salesResponse.success) {
        setSales(salesResponse.data);
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch sales orders');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
  }, []);

  const handleDeleteClick = (sale) => {
    setSelectedSale(sale);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteSale(selectedSale.id);
      if (response.success) {
        await fetchSales();
        setDeleteDialogOpen(false);
        setSelectedSale(null);
      }
    } catch (err) {
      console.error('Error deleting sale:', err);
    }
  };

  const handleStatusClick = (sale) => {
    setSelectedSale(sale);
    setStatusData({
      status: sale.status,
      payment_status: sale.payment_status
    });
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    try {
      const response = await updateSaleStatus(selectedSale.id, statusData);
      if (response.success) {
        await fetchSales();
        setStatusDialogOpen(false);
        setSelectedSale(null);
        setStatusData({ status: '', payment_status: '' });
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleCreateSale = () => {
    navigate('/sales/new');
  };

  const handleViewSale = (id) => {
    navigate(`/sales/${id}`);
  };

  const handleEditSale = (id) => {
    navigate(`/sales/edit/${id}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-LK');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Sales Management
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSales}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateSale}
          >
            New Sale
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Sales
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalSales || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalRevenue || 'LKR 0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Today's Sales
              </Typography>
              <Typography variant="h5" component="div">
                {stats.todaySales || 'LKR 0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Orders
              </Typography>
              <Typography variant="h5" component="div">
                {stats.pendingSales || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Sales Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Delivery Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No sales orders found
                  </TableCell>
                </TableRow>
              ) : (
                sales.map((sale) => (
                  <TableRow key={sale.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {sale.order_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {sale.Customer?.name || 'N/A'}
                    </TableCell>
                    <TableCell>{formatDate(sale.order_date)}</TableCell>
                    <TableCell>{sale.delivery_date ? formatDate(sale.delivery_date) : 'N/A'}</TableCell>
                    <TableCell>{sale.total_amount}</TableCell>
                    <TableCell>
                      <Chip
                        label={sale.status}
                        color={statusColors[sale.status]}
                        size="small"
                        onClick={() => handleStatusClick(sale)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={sale.payment_status}
                        color={paymentStatusColors[sale.payment_status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
  size="small"
  onClick={() => navigate(`/sales/view/${sale.id}`)}  // Changed from handleViewSale
  title="View"
>
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditSale(sale.id)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(sale)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete sales order {selectedSale?.order_number}?
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Update Sales Order Status</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Order Status"
                value={statusData.status}
                onChange={(e) => setStatusData(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Confirmed">Confirmed</MenuItem>
                <MenuItem value="Processing">Processing</MenuItem>
                <MenuItem value="Shipped">Shipped</MenuItem>
                <MenuItem value="Delivered">Delivered</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Payment Status"
                value={statusData.payment_status}
                onChange={(e) => setStatusData(prev => ({ ...prev, payment_status: e.target.value }))}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Partial">Partial</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Sales;